import type { ChangeEvent, RefObject } from 'react';
import { useCallback, useState } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';

import { usePromoteFormData, usePromoteFormState } from '../../../../../../formContext';
import { useTracking } from '../../../../../../shared/hooks/useTracking';

import { useButtonAmounts } from './hooks/useButtonAmounts';
import { useCustomAmount } from './hooks/useCustomAmount';

type Result = ModelHookResult<
	{
		buttonAmounts: Array<ReturnType<typeof useButtonAmounts>[number]>;
		selection: string | undefined;
		customAmount: string;
		customInputRef: RefObject<HTMLInputElement | null>;
		amountErrorText: string | null;
		customAmountLabel: string;
		currencySymbol: string;
		currencySymbolPosition: 'before' | 'after';
	},
	{
		handleAmountChange: (value: string) => void;
		handleCustomAmountFocus: () => void;
		handleCustomAmountChange: (value: ChangeEvent<HTMLInputElement>) => void;
	}
>;

export function useAmountSelector(): Result {
	const {
		amountChoices,
		currency: { code: currencyCode },
	} = usePromoteFormData();
	const [{ amount: amountInPageState, validationErrors }, { setAmount, setCustomAmountSelected, setValidationErrors }] =
		usePromoteFormState();
	const { translate, getCurrencySymbol, getCurrencySymbolPosition } = useI18n();
	const { trackAmountChanged } = useTracking();
	const [selection, setSelection] = useState<string | undefined>(() => {
		if (!amountInPageState) return undefined;
		if (amountChoices.some((choice) => choice.amount === amountInPageState)) {
			return `${amountInPageState}`;
		}
		return 'custom';
	});

	const buttonAmounts = useButtonAmounts();
	const customAmount = useCustomAmount();

	const handleAmountChange = useCallback(
		(value: string): void => {
			setSelection(value);
			setCustomAmountSelected(value === 'custom');
			if (value !== 'custom') {
				const valueAsNumber = Number(value);
				const buttonIndex = amountChoices.findIndex((choice) => choice.amount === valueAsNumber) + 1;
				setValidationErrors({ ...validationErrors, amountSelection: '' });
				setAmount(valueAsNumber);
				trackAmountChanged({ buttonWasClicked: true, buttonClicked: `p${buttonIndex}` });
			} else {
				customAmount.actions.handleCustomAmountSelect();
			}
		},
		[
			amountChoices,
			setAmount,
			setCustomAmountSelected,
			setValidationErrors,
			trackAmountChanged,
			customAmount.actions,
			validationErrors,
		],
	);

	return {
		data: {
			buttonAmounts,
			selection,
			...customAmount.data,
			amountErrorText: validationErrors ? validationErrors.amount : null,
			customAmountLabel: translate('fe.pages.sponsors.other_amount'),
			currencySymbol: getCurrencySymbol(currencyCode),
			currencySymbolPosition: getCurrencySymbolPosition(currencyCode),
		},
		actions: {
			handleAmountChange,
			handleCustomAmountFocus: customAmount.actions.handleCustomAmountFocus,
			handleCustomAmountChange: customAmount.actions.handleCustomAmountChange,
		},
	};
}
